"use client";

import { useCallback, useEffect } from "react";
import { Notebook } from "../client";
import { StudyDocument, StudyNotUploadedDocument } from "../types";
import { ChatInterface } from "./layout/chat-interface";
import { makeModelAdapter } from "../hooks/useChatRuntime";
import { AssistantRuntimeProvider, useLocalRuntime } from "@assistant-ui/react";
import { useNotebookContext } from "../hooks/useNotebook";
import { SidebarProvider } from "@/components/ui/sidebar";
import { toast } from "sonner";
import { useCommunication } from "@/lib/CommunicationWrapper";
import { CanvasCourse } from "@/lib/types";

interface FileWorkspaceProps {
  course: CanvasCourse;
  file: StudyNotUploadedDocument;
  returnUrl: string;
}

export function FileWorkspace({ course, file, returnUrl }: FileWorkspaceProps) {
  const {
    notebook: selectedNotebook,
    selectedSources,
    editNotebook,
    addSource,
    createNotebook,
    currentPage,
    navigatePage,
  } = useNotebookContext();

  function renameNotebook(name: string) {
    editNotebook({
      ...selectedNotebook,
      name: name,
    });
  }

  const {
    sendMessageAndListen,
    navigatePage: navigateBCPage,
    openGate,
  } = useCommunication();

  useEffect(() => {
    if (
      currentPage.type === "highlight" &&
      selectedSources.length &&
      selectedNotebook
    ) {
      const fileSource = selectedSources.find(
        (s) =>
          "canvas_file_id" in s.metadata &&
          s.metadata["canvas_file_id"] === file.metadata["canvas_file_id"]
      );
      navigateBCPage("study", {
        notebook_id: selectedNotebook.id.toString(),
        highlight_id: currentPage.value.id.toString(),
        ...(fileSource ? { document_id: fileSource.id.toString() } : {}),
        return: returnUrl,
      });
    }
  }, [currentPage, selectedNotebook, selectedSources, file]);

  async function uploadCourseSource(
    document: StudyNotUploadedDocument,
    notebookId: string
  ) {
    // const loadingToastId = toast.loading("Processing file…", {
    //   description: <span className="text-theme-1">{document.name}</span>,
    // });
    const result = await sendMessageAndListen({
      type: "CALL_EXTENSION_FUNC",
      payload: {
        func: "uploadStudyDocument",
        args: { document: document, notebook: notebookId },
      },
    });

    // toast.dismiss(loadingToastId);

    if (!result.data || !result.success) {
      if (result.status === 402) {
        openGate("study_files");
      } else {
        toast.error(`Failed to process course document.`, {
          description: (
            <span className="text-theme-1">{JSON.stringify(result.error)}</span>
          ),
        });
      }
      return;
    }

    // toast.success("File processed", {
    //   description: <span className="text-theme-1">{document.name}</span>,
    // });
    addSource(result.data);

    return result.data as StudyDocument;
  }

  const makeAdapter = useCallback(() => {
    return makeModelAdapter(selectedNotebook.id.toString(), selectedSources, {
      courseFile: file,
      course,
      createNotebook,
      uploadCourseSource,
    });
  }, [selectedNotebook, selectedSources, course, file]);

  const runtime = useLocalRuntime(makeAdapter(), {});

  return (
    <div className="h-full w-full bg-theme-0">
      <SidebarProvider className="h-full">
        <AssistantRuntimeProvider runtime={runtime}>
          <ChatInterface
            notebookName={selectedNotebook?.name || "File Chat"}
            selectedSourcesCount={1}
            totalSourcesCount={1}
            runtime={runtime}
            onRenameNotebook={renameNotebook}
          />
        </AssistantRuntimeProvider>
      </SidebarProvider>
    </div>
  );
}
